import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage, injectIntl } from 'react-intl';
import { BasicModal, Collapsible, DefaultButton } from 'common/components';
import { BaseSelectField, FileField, TextAreaField } from 'common/components/form-fields';
import { withTheme, taggedStylesToString } from 'common/styling/theme';
import { listBankProofs, uploadBankProof, archiveBankProof, reviewBankProof } from './api';
import BankProofDocument from './BankProofDocument';
import { staticStyles, getDynamicStyles } from './style';

const statusMessages = {
  pending: 'bankProofPending',
  approved: 'bankProofApproved',
  rejected: 'bankProofRejected',
  revoked: 'bankProofRevoked',
};

const checkResponse = response => {
  if (response.status >= 300) throw response;
  return response.data;
};

export const BankProofPanel = ({ userId, canReview, onChange, intl, theme }) => {
  const dynamicStyles = Object.keys(theme).length ? taggedStylesToString(getDynamicStyles(theme)) : '';
  const [proofs, setProofs] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');
  const [file, setFile] = useState(null);
  const [replaces, setReplaces] = useState(null);
  const [review, setReview] = useState(null);
  const [reason, setReason] = useState('');
  const [fileKey, setFileKey] = useState(0);
  const uploadRef = useRef(null);
  const isStaff = Boolean(userId);

  useEffect(() => {
    if (replaces) uploadRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [replaces]);

  const showError = useCallback(
    err => {
      const data = err.response?.data || err.data;
      const message = data?.detail || data?.file?.[0] || data?.reason?.[0];
      setError(typeof message === 'string' ? message : intl.formatMessage({ id: 'bankProofRequestFailed' }));
    },
    [intl]
  );

  const reload = useCallback(async () => {
    const data = checkResponse(await listBankProofs(userId, filter));
    setProofs(data);
    setLoaded(true);
  }, [userId, filter]);

  useEffect(() => {
    let active = true;
    setLoaded(false);
    setError('');
    listBankProofs(userId, filter)
      .then(response => {
        const data = checkResponse(response);
        if (active) {
          setProofs(data);
          setLoaded(true);
        }
      })
      .catch(err => {
        if (active) showError(err);
      });
    return () => {
      active = false;
    };
  }, [userId, filter, showError]);

  const run = async action => {
    setBusy(true);
    setError('');
    try {
      await action();
      await reload();
      if (onChange) onChange();
    } catch (err) {
      showError(err);
    } finally {
      setBusy(false);
    }
  };

  const submit = event => {
    event.preventDefault();
    if (!file) return;
    run(async () => {
      checkResponse(await uploadBankProof(file, replaces));
      setFile(null);
      setReplaces(null);
      setFileKey(key => key + 1);
    });
  };

  const submitReview = event => {
    event.preventDefault();
    run(async () => {
      checkResponse(await reviewBankProof(userId, review.id, review.action, reason));
      setReview(null);
      setReason('');
    });
  };

  const filterOptions = [{ value: '', label: intl.formatMessage({ id: 'bankProofAll' }) }].concat(
    Object.entries(statusMessages).map(([value, id]) => ({ value, label: intl.formatMessage({ id }) }))
  );

  const selectFile = files => {
    const selected = files[0];
    if (selected?.size > 10 * 1024 * 1024) {
      setFile(null);
      setError(intl.formatMessage({ id: 'bankProofFileTooLarge' }));
    } else {
      setFile(selected || null);
      setError('');
    }
  };

  const content = (
    <div className="BankProofPanel__content" aria-busy={busy || (!loaded && !error)}>
      {error && !review && (
        <p role="alert" className="BankProofPanel__error">
          {error}
        </p>
      )}
      {!isStaff && (
        <form onSubmit={submit} className="BankProofPanel__upload" ref={uploadRef}>
          <h2 className="BankProofPanel__title">
            <FormattedMessage id={replaces ? 'bankProofReplace' : 'bankProofUpload'} />
          </h2>
          <p className="BankProofPanel__instructions">
            <FormattedMessage id="bankProofInstructions" />
          </p>
          {replaces && (
            <p className="BankProofPanel__notice" role="status">
              <FormattedMessage id="bankProofReplacementNotice" values={{ id: replaces }} />
            </p>
          )}
          <FileField
            key={fileKey}
            input={{ name: 'bank-proof-file', value: file ? [file] : null, onChange: selectFile }}
            fileNameId="justDocument"
            formatNameId="documentsFormats"
            accept=".png,.jpg,.jpeg,.pdf,.doc,.docx"
            maxSize={10 * 1024 * 1024}
            disabled={busy}
            withCrossButton
            handleDeleteFile={() => setFile(null)}
            onSizeError={({ error: invalid }) => {
              if (invalid) setFile(null);
            }}
          />
          {file && <p className="BankProofPanel__filename">{file.name}</p>}
          <div className="BankProofPanel__actions">
            <DefaultButton
              textId={replaces ? 'bankProofReplace' : 'bankProofUpload'}
              type="submit"
              filled
              disabled={busy || !file}
            />
            {replaces && <DefaultButton textId="bankProofCancel" disabled={busy} onClick={() => setReplaces(null)} />}
          </div>
        </form>
      )}
      <div className="BankProofPanel__toolbar">
        {isStaff ? (
          <div className="BankProofPanel__filter">
            <BaseSelectField
              name="bank-proof-status"
              textId="bankProofFilter"
              value={filterOptions.find(option => option.value === filter)}
              options={filterOptions}
              onChange={option => setFilter(option.value)}
              disabled={busy}
            />
          </div>
        ) : (
          <h2 className="BankProofPanel__title">
            <FormattedMessage id="justDocuments" />
          </h2>
        )}
        <DefaultButton textId="bankProofRefresh" disabled={busy} onClick={() => run(async () => {})} />
      </div>
      {!loaded && !error && (
        <p role="status" className="BankProofPanel__empty">
          <FormattedMessage id="bankProofLoading" />
        </p>
      )}
      {loaded && !proofs.length && (
        <p className="BankProofPanel__empty">
          <FormattedMessage id="bankProofEmpty" />
        </p>
      )}
      <div className="BankProofPanel__list" role="list">
        {(loaded ? proofs : []).map(proof => (
          <div
            className={classNames('BankProofPanel__document', { 'BankProofPanel__document--preview': isStaff })}
            role="listitem"
            key={proof.id}>
            {isStaff && <BankProofDocument userId={userId} proofId={proof.id} filename={proof.filename} />}
            <div className="BankProofPanel__details">
              <strong className="BankProofPanel__filename">{proof.filename}</strong>
              <div className="BankProofPanel__metadata">
                <span className="BankProofPanel__status" data-status={proof.archivedAt ? 'archived' : proof.status}>
                  <FormattedMessage id={proof.archivedAt ? 'bankProofArchived' : statusMessages[proof.status]} />
                </span>
                <time dateTime={proof.created}>{intl.formatDate(proof.created)}</time>
              </div>
              {proof.reason && <p className="BankProofPanel__reason">{proof.reason}</p>}
              {isStaff && proof.reviewer && (
                <p className="BankProofPanel__reviewer">
                  {proof.reviewer} · {intl.formatDate(proof.reviewedAt)} {intl.formatTime(proof.reviewedAt)}
                </p>
              )}
            </div>
            <div className="BankProofPanel__actions">
              {!proof.archivedAt && !isStaff && (
                <>
                  <DefaultButton textId="bankProofReplace" disabled={busy} onClick={() => setReplaces(proof.id)} />
                  <DefaultButton
                    textId="bankProofArchive"
                    disabled={busy}
                    onClick={() =>
                      run(async () => {
                        checkResponse(await archiveBankProof(proof.id));
                        if (replaces === proof.id) setReplaces(null);
                      })
                    }
                  />
                </>
              )}
              {!proof.archivedAt && canReview && proof.status === 'pending' && (
                <>
                  <DefaultButton
                    textId="bankProofApprove"
                    butColor="success"
                    disabled={busy}
                    onClick={() =>
                      run(async () => {
                        checkResponse(await reviewBankProof(userId, proof.id, 'approve', ''));
                      })
                    }
                  />
                  <DefaultButton
                    textId="bankProofReject"
                    butColor="error"
                    disabled={busy}
                    onClick={() => {
                      setReview({ id: proof.id, filename: proof.filename, action: 'reject' });
                      setReason('');
                    }}
                  />
                </>
              )}
              {!proof.archivedAt && canReview && proof.status === 'approved' && (
                <DefaultButton
                  textId="bankProofRevoke"
                  butColor="error"
                  disabled={busy}
                  onClick={() => {
                    setReview({ id: proof.id, filename: proof.filename, action: 'revoke' });
                    setReason('');
                  }}
                />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <section className="BankProofPanel">
      {isStaff ? <Collapsible captionId="bankVerificationTitle">{content}</Collapsible> : content}
      {review && (
        <BasicModal
          isOpen
          captionId={review.action === 'reject' ? 'bankProofReject' : 'bankProofRevoke'}
          onRequestClose={() => {
            if (!busy) setReview(null);
          }}>
          <form onSubmit={submitReview} className="BankProofPanel__form">
            <p className="BankProofPanel__filename">{review.filename}</p>
            {error && (
              <p role="alert" className="BankProofPanel__error">
                {error}
              </p>
            )}
            <TextAreaField
              input={{ name: 'bank-proof-reason', value: reason, onChange: setReason, maxLength: 2000, required: true }}
              textId="bankProofReason"
              rows="4"
              cols="40"
              meta={{ touched: false, active: false }}
              disabled={busy}
            />
            <div className="BankProofPanel__actions">
              <DefaultButton
                textId={review.action === 'reject' ? 'bankProofReject' : 'bankProofRevoke'}
                type="submit"
                butColor="error"
                disabled={busy || !reason.trim()}
              />
              <DefaultButton textId="bankProofCancel" disabled={busy} onClick={() => setReview(null)} />
            </div>
          </form>
        </BasicModal>
      )}
      <style jsx global>
        {staticStyles}
      </style>
      <style>{dynamicStyles}</style>
    </section>
  );
};

BankProofPanel.propTypes = {
  intl: PropTypes.object.isRequired,
  userId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  canReview: PropTypes.bool,
  onChange: PropTypes.func,
  theme: PropTypes.object,
};
BankProofPanel.defaultProps = { userId: null, canReview: false, onChange: null, theme: {} };
export default withTheme()(injectIntl(BankProofPanel));
